/* ----------------------------------------------- */
/*   Reusable selectors to use in mapStateToProps
/*   so that the props only change when the actual bit of state they depend on changes
/*   https://redux.js.org/recipes/computing-derived-data
/*   https://redux-starter-kit.js.org/api/createselector
/*   https://github.com/reduxjs/reselect
/* ----------------------------------------------- */

import { createSelector } from 'redux-starter-kit';
import { MOBILE, FIXED } from 'AppSrc/core/components/forms/value-dependency-constants';
import { COORDINATOR } from 'AppSrc/core/components/forms/form-base-components/data-structure/constants';
import { getArraySortedByProperty } from 'AppSrc/core/utility-functions/sort-functions';

const PREFERRED_COORDINATOR_GROUPS = {
  [MOBILE]: 'Change Management',
  [FIXED]: 'General Change User',
};

const isTokenSavedInSyncedStorageSelector = (state) => (state.auth.isTokenSavedInSyncedStorage);
const tokenSelector = (state) => (state.auth.token);
const SAMLAuthUrlSelector = (state) => (state.auth.SAMLAuthUrl);
const isSAMLAuthUrlFetchingSelector = (state) => (state.auth.isSAMLAuthUrlFetching);
const SAMLAuthErrorSelector = (state) => (state.auth.SAMLAuthError);
const isAuthenticatingSelector = (state) => (state.auth.isAuthenticating);
const isTokenCheckingSelector = (state) => (state.auth.isTokenChecking);
const userSelector = (state) => (state.auth.user);
const userRolesSelector = (state) => (state.auth.user.roles);
const userGroupsSelector = (state) => (state.auth.user.groups);

const authenticatedSelector = createSelector(
  tokenSelector,
  (token) => (token !== null),
);

const hasSAMLAuthErrorSelector = createSelector(
  SAMLAuthErrorSelector,
  (error) => (error !== null),
);

const usernameSelector = createSelector(
  userSelector,
  (user) => `${user.firstName} ${user.lastName}`,
);

// selector to get the users groups for both networks
// maintaining info on which network the group belongs to
// and ordered alphabetically. The first group should have a
// default flag added
// the groups for a network should only be included if the user is coordinator on that network
// E.G > [{name: 'group1', network: MOBILE, default: true }, .... ];
const orderedGroupsSelector = createSelector(
  userGroupsSelector,
  userRolesSelector,
  (userGroups, roles) => {
    const networks = [MOBILE, FIXED];
    // an array that will contain group objects of the form { name: 'group name', network: MOBILE }
    const groups = [];
    networks.forEach((network) => {
      const coordinatorForNetwork = roles[network] === COORDINATOR;
      const groupsForNetwork = userGroups[network] || [];
      if (coordinatorForNetwork) {
        groupsForNetwork.forEach((name) => {
          const group = {
            name,
            network,
          };
          groups.push(group);
        });
      }
    });
    // sort the groups alphabetically
    const orderedGroups = getArraySortedByProperty(groups, 'name');
    // add default flag to the first group
    if (orderedGroups.length > 0) {
      orderedGroups[0].default = true;
    }
    return orderedGroups;
  },
);

// selector to get the users default coordinator group for each network
// this is the first group in the array, unless the preferred group for each network is included
const defaultUserGroupSelector = createSelector(
  userGroupsSelector,
  userRolesSelector,
  (userGroups, roles) => {
    const networks = [MOBILE, FIXED];
    // an object with the default group for each network
    const defaultGroupByNetwork = {
      [MOBILE]: null,
      [FIXED]: null,
    };
    networks.forEach((network) => {
      const coordinatorForNetwork = roles[network] === COORDINATOR;
      const groupsForNetwork = userGroups[network] || [];
      if (coordinatorForNetwork) {
        const preferredGroupForNetwork = PREFERRED_COORDINATOR_GROUPS[network];
        const userHasPreferredGroup = groupsForNetwork.includes(preferredGroupForNetwork);
        // set the default group for the network to the preferred group if the user is a member of it
        // otherwise just set the first group the user is a member of
        defaultGroupByNetwork[network] = userHasPreferredGroup ? preferredGroupForNetwork : groupsForNetwork[0];
      }
    });
    return defaultGroupByNetwork;
  },
);

export {
  isTokenSavedInSyncedStorageSelector,
  tokenSelector,
  SAMLAuthUrlSelector,
  isSAMLAuthUrlFetchingSelector,
  SAMLAuthErrorSelector,
  hasSAMLAuthErrorSelector,
  isAuthenticatingSelector,
  isTokenCheckingSelector,
  authenticatedSelector,
  userSelector,
  userRolesSelector,
  usernameSelector,
  orderedGroupsSelector,
  defaultUserGroupSelector,
};
